import React, { createContext, useContext, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { combineResourceRewardAlerts, filterUnseenRewardAlert } from '../utils/rewardAlertIdentity';

// Split into two contexts to prevent unnecessary re-renders:
// - ActionsContext provides stable functions (queueAlert, dismissCurrentAlert)
// - StateContext provides currentAlert (only AlertContainer subscribes)
const AlertQueueActionsContext = createContext();
const AlertQueueStateContext = createContext();
const acceptUntrackedRewardAlert = (alert) => alert;

const ALERT_BATCH_DEBOUNCE_MS = 250;
const ALERT_DISMISS_GAP_MS = 500;
const ALERT_BATCH_BASE_DURATION_MS = 10000;
const ALERT_BATCH_EXTRA_PER_SECTION_MS = 2500;
const ALERT_BATCH_MAX_DURATION_MS = 18000;
const CTA_ELIGIBLE_ALERT_TYPES = new Set(['gettoncini', 'learned_terms', 'stelline']);

// For components that need to queue alerts (games, etc.)
export const useAlertQueueActions = () => {
  const context = useContext(AlertQueueActionsContext);
  if (!context) {
    // Return no-op functions when no provider exists (admin portal)
    return {
      queueAlert: () => {},
      dismissCurrentAlert: () => {},
      setAlertAccount: () => {},
      acceptRewardAlert: acceptUntrackedRewardAlert,
      registerRewardPresenter: () => () => {},
    };
  }
  return context;
};

// For components that need to display the current alert (AlertContainer only)
export const useAlertQueueState = () => {
  const context = useContext(AlertQueueStateContext);
  if (!context) {
    // Return safe defaults when no provider exists (admin portal)
    return { currentAlert: null, hasPendingAlerts: false };
  }
  return context;
};

// Legacy hook for backwards compatibility
export const useAlertQueue = () => {
  const actions = useAlertQueueActions();
  const state = useAlertQueueState();
  return { ...actions, ...state };
};

const resolveBatchDurationMs = (alerts = []) => {
  const normalizedAlerts = Array.isArray(alerts) ? alerts : [];
  const longestSectionDurationMs = normalizedAlerts.reduce((maxDuration, alert) => {
    const sectionDuration = Number(alert?.duration);
    if (Number.isFinite(sectionDuration) && sectionDuration > maxDuration) {
      return sectionDuration;
    }
    return maxDuration;
  }, 0);
  const baselineDurationMs = Math.max(ALERT_BATCH_BASE_DURATION_MS, longestSectionDurationMs);
  const extraSectionsCount = Math.max(0, normalizedAlerts.length - 1);
  const scaledDurationMs = baselineDurationMs + (extraSectionsCount * ALERT_BATCH_EXTRA_PER_SECTION_MS);
  return Math.min(ALERT_BATCH_MAX_DURATION_MS, scaledDurationMs);
};

const shouldDisableAutoDismissForBatch = (alerts = []) => {
  const normalizedAlerts = Array.isArray(alerts) ? alerts : [];
  return normalizedAlerts.some((alert) => (
    alert
    && typeof alert === 'object'
    && alert.isAnonymous === true
    && CTA_ELIGIBLE_ALERT_TYPES.has(alert.type)
  ));
};

const buildBatchFromAlerts = (alerts = [], delayMs = 0) => ({
  type: 'combined_reward_alert',
  alerts,
  delay: Math.max(0, Number(delayMs) || 0),
  duration: resolveBatchDurationMs(alerts),
  autoDismiss: !shouldDisableAutoDismissForBatch(alerts),
});

const storageKey = (key) => `accepted-resource-rewards-v1:${key}`;
const loadSeenRewards = (key) => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey(key)) || '[]');
    return new Set(Array.isArray(stored) ? stored.filter((id) => typeof id === 'string') : []);
  } catch (_) {
    return new Set();
  }
};

export const AlertQueueProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [isAlertInFlight, setIsAlertInFlight] = useState(false);
  const queueRef = useRef([]);
  const rewardPresentersRef = useRef(new Map());
  const ownerRef = useRef({ key: 'anonymous', generation: 0, initialized: false });
  const [ownerGeneration, setOwnerGeneration] = useState(0);
  const seenRewardsRef = useRef(null);
  if (seenRewardsRef.current === null) seenRewardsRef.current = loadSeenRewards(ownerRef.current.key);

  const isProcessingRef = useRef(false);
  const isFirstBatchRef = useRef(true);
  const durationTimeoutRef = useRef(null);
  const delayTimeoutRef = useRef(null);
  const transitionTimeoutRef = useRef(null);

  const pendingBatchAlertsRef = useRef([]);
  const pendingBatchFirstDelayRef = useRef(0);
  const batchDebounceTimeoutRef = useRef(null);
  const [hasPendingDebouncedBatch, setHasPendingDebouncedBatch] = useState(false);

  const clearTimer = (ref) => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const clearAllVisibleAlertTimers = useCallback(() => {
    clearTimer(delayTimeoutRef);
    clearTimer(durationTimeoutRef);
    clearTimer(transitionTimeoutRef);
  }, []);

  const clearBatchDebounceTimer = useCallback(() => {
    clearTimer(batchDebounceTimeoutRef);
  }, []);

  const processNextAlert = useCallback(() => {
    if (queueRef.current.length === 0) {
      isProcessingRef.current = false;
      setCurrentAlert(null);
      setIsAlertInFlight(false);
      isFirstBatchRef.current = true;
      return;
    }

    const [nextBatch, ...remainingQueue] = queueRef.current;
    queueRef.current = remainingQueue;
    setQueue(remainingQueue);
    clearAllVisibleAlertTimers();
    isProcessingRef.current = true;
    setIsAlertInFlight(true);

    const effectiveDelay = isFirstBatchRef.current ? (nextBatch.delay || 0) : 0;
    delayTimeoutRef.current = setTimeout(() => {
      setCurrentAlert(nextBatch);
      isFirstBatchRef.current = false;

      if (nextBatch.autoDismiss === false) {
        return;
      }

      durationTimeoutRef.current = setTimeout(() => {
        setCurrentAlert(null);
        transitionTimeoutRef.current = setTimeout(() => {
          processNextAlert();
        }, ALERT_DISMISS_GAP_MS);
      }, nextBatch.duration || ALERT_BATCH_BASE_DURATION_MS);
    }, effectiveDelay);
  }, [clearAllVisibleAlertTimers]);

  const flushPendingBatchToQueue = useCallback(() => {
    clearBatchDebounceTimer();

    const pendingAlerts = pendingBatchAlertsRef.current;
    if (!pendingAlerts.length) {
      setHasPendingDebouncedBatch(false);
      return;
    }

    const batch = buildBatchFromAlerts(
      combineResourceRewardAlerts(pendingAlerts),
      pendingBatchFirstDelayRef.current
    );

    pendingBatchAlertsRef.current = [];
    pendingBatchFirstDelayRef.current = 0;
    setHasPendingDebouncedBatch(false);

    queueRef.current = [...queueRef.current, batch];
    setQueue(queueRef.current);
  }, [clearBatchDebounceTimer]);

  const acceptRewardAlert = useCallback((alert) => {
    if (ownerGeneration !== ownerRef.current.generation) return null;
    const uniqueAlert = filterUnseenRewardAlert(alert, seenRewardsRef.current);
    if (!uniqueAlert) return null;
    ownerRef.current.initialized = true;
    try {
      sessionStorage.setItem(storageKey(ownerRef.current.key), JSON.stringify([...seenRewardsRef.current]));
    } catch (_) {}
    return uniqueAlert;
  }, [ownerGeneration]);

  const registerRewardPresenter = useCallback((type, presenter) => {
    rewardPresentersRef.current.set(type, presenter);
    return () => {
      if (rewardPresentersRef.current.get(type) === presenter) {
        rewardPresentersRef.current.delete(type);
      }
    };
  }, []);

  const queueAlert = useCallback((alert) => {
    const uniqueAlert = acceptRewardAlert(alert);
    if (!uniqueAlert) return false;

    // Presenters receive an already accepted reward, sharing snackbar deduplication.
    if (rewardPresentersRef.current.get(uniqueAlert.type)?.(uniqueAlert)) return true;

    const normalizedAlert = { ...uniqueAlert, delay: 0 };
    if (pendingBatchAlertsRef.current.length === 0) {
      pendingBatchFirstDelayRef.current = Math.max(0, Number(alert.delay) || 0);
    }

    pendingBatchAlertsRef.current = [...pendingBatchAlertsRef.current, normalizedAlert];
    setHasPendingDebouncedBatch(true);

    clearBatchDebounceTimer();
    batchDebounceTimeoutRef.current = setTimeout(() => {
      flushPendingBatchToQueue();
    }, ALERT_BATCH_DEBOUNCE_MS);
    return true;
  }, [acceptRewardAlert, clearBatchDebounceTimer, flushPendingBatchToQueue]);

  const setAlertAccount = useCallback((key) => {
    if (!key || ownerRef.current.key === key) return;
    if (!ownerRef.current.initialized) {
      ownerRef.current = { key, generation: 0, initialized: true };
      seenRewardsRef.current = loadSeenRewards(key);
      return;
    }
    ownerRef.current = { key, generation: ownerRef.current.generation + 1, initialized: true };
    seenRewardsRef.current = loadSeenRewards(key);
    clearAllVisibleAlertTimers();
    clearBatchDebounceTimer();
    queueRef.current = [];
    pendingBatchAlertsRef.current = [];
    pendingBatchFirstDelayRef.current = 0;
    isProcessingRef.current = false;
    isFirstBatchRef.current = true;
    setQueue([]);
    setCurrentAlert(null);
    setIsAlertInFlight(false);
    setHasPendingDebouncedBatch(false);
    setOwnerGeneration(ownerRef.current.generation);
  }, [clearAllVisibleAlertTimers, clearBatchDebounceTimer]);

  // Start queue processing only after queued alerts are committed to state.
  useEffect(() => {
    if (!isProcessingRef.current && queue.length > 0) {
      isProcessingRef.current = true;
      processNextAlert();
    }
  }, [queue.length, processNextAlert]);

  const dismissCurrentAlert = useCallback(() => {
    clearAllVisibleAlertTimers();
    setCurrentAlert(null);
    setIsAlertInFlight(true);
    transitionTimeoutRef.current = setTimeout(() => {
      processNextAlert();
    }, ALERT_DISMISS_GAP_MS);
  }, [clearAllVisibleAlertTimers, processNextAlert]);

  useEffect(() => {
    // Strict Mode replays effect setup after cleanup; resume an accepted batch.
    if (pendingBatchAlertsRef.current.length && !batchDebounceTimeoutRef.current) {
      batchDebounceTimeoutRef.current = setTimeout(flushPendingBatchToQueue, ALERT_BATCH_DEBOUNCE_MS);
    }
    return () => {
      clearAllVisibleAlertTimers();
      clearBatchDebounceTimer();
    };
  }, [clearAllVisibleAlertTimers, clearBatchDebounceTimer, flushPendingBatchToQueue]);

  // Memoize actions separately - these never change
  const actions = useMemo(() => ({
    queueAlert,
    dismissCurrentAlert,
    setAlertAccount,
    acceptRewardAlert,
    registerRewardPresenter,
  }), [queueAlert, dismissCurrentAlert, setAlertAccount, acceptRewardAlert, registerRewardPresenter]);

  // Memoize state separately - this changes when alerts show/hide
  const state = useMemo(() => ({
    currentAlert,
    hasPendingAlerts:
      currentAlert !== null ||
      queue.length > 0 ||
      hasPendingDebouncedBatch ||
      isAlertInFlight,
  }), [currentAlert, queue.length, hasPendingDebouncedBatch, isAlertInFlight]);

  return (
    <AlertQueueActionsContext.Provider value={actions}>
      <AlertQueueStateContext.Provider value={state}>
        {children}
      </AlertQueueStateContext.Provider>
    </AlertQueueActionsContext.Provider>
  );
};
